import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trophy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { bewertungAPI } from '@/lib/api';

/* v8 M20: Turniererfolg von Hand eintragen (das CRM kennt keine Turnierdaten). Löst — wenn die Bewertungs-Automation an ist —
   einen Entwurf „Google-Bewertung anfragen“ in der Freigabe-Zentrale aus. */

const ERGEBNIS = { gold: 'Gold', silber: 'Silber', bronze: 'Bronze', sieg: 'Sieg' };
const tagText = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '');
const heute = () => new Date().toISOString().slice(0, 10);

export default function TurniererfolgKarte({ leadId, darf }) {
  const [liste, setListe] = useState([]);
  const [offen, setOffen] = useState(false);
  const [turnier, setTurnier] = useState('');
  const [ergebnis, setErgebnis] = useState('gold');
  const [datum, setDatum] = useState(heute());
  const [laeuft, setLaeuft] = useState(false);

  const laden = useCallback(() => {
    bewertungAPI.turniererfolge(leadId).then((r) => setListe(r.data?.eintraege || [])).catch(() => setListe([]));
  }, [leadId]);
  useEffect(() => { laden(); }, [laden]);

  const speichern = async () => {
    if (!turnier.trim()) { toast.error('Turnier angeben'); return; }
    setLaeuft(true);
    try {
      await bewertungAPI.turniererfolgEintragen({ lead_id: leadId, turnier: turnier.trim(), ergebnis, datum });
      toast.success('Turniererfolg eingetragen');
      setTurnier(''); setOffen(false); laden();
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Eintragen fehlgeschlagen');
    } finally { setLaeuft(false); }
  };

  if (!darf && liste.length === 0) return null;
  return (
    <Card className="border-border/60" data-testid="turniererfolg-karte">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2"><Trophy className="w-4 h-4" /> Turniererfolge</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {liste.length > 0 && (
          <ul className="text-sm space-y-1">
            {liste.map((t) => <li key={t.id}>{ERGEBNIS[t.ergebnis] || t.ergebnis} · {t.turnier} · {tagText(t.datum)}</li>)}
          </ul>
        )}
        {darf && !offen && <Button size="sm" variant="outline" onClick={() => setOffen(true)}>Turniererfolg eintragen</Button>}
        {darf && offen && (
          <div className="grid gap-2 sm:grid-cols-3 max-w-xl">
            <div className="sm:col-span-3">
              <Label className="text-xs">Turnier</Label>
              <Input value={turnier} onChange={(e) => setTurnier(e.target.value)} maxLength={120} />
            </div>
            <div>
              <Label className="text-xs">Ergebnis</Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={ergebnis} onChange={(e) => setErgebnis(e.target.value)}>
                {Object.entries(ERGEBNIS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Datum</Label>
              <Input type="date" value={datum} max={heute()} onChange={(e) => setDatum(e.target.value)} />
            </div>
            <div className="flex items-end gap-2">
              <Button size="sm" className="bg-marke text-white hover:bg-marke/90" onClick={speichern} disabled={laeuft}>Speichern</Button>
              <Button size="sm" variant="ghost" onClick={() => setOffen(false)}>Abbrechen</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
